import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { profileAPI } from '../lib/supabase'
import LocationPicker from './LocationPicker'
import PageMeta from './PageMeta'

function ProfileSetup() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, profile: contextProfile, loading: authLoading, loadUserProfile } = useAuth()
  
  // Get user data from navigation state or auth context
  const userData = location.state || {}
  const authUser = userData.user || user
  const existingProfile = userData.existingProfile || contextProfile // Check if profile already exists
  
  const [formData, setFormData] = useState({
    username: existingProfile?.username || '',
    display_name: existingProfile?.display_name || userData.name || authUser?.user_metadata?.full_name || authUser?.user_metadata?.name || '',
    country: existingProfile?.country || '',
    city: existingProfile?.city || '',
    avatar_url: existingProfile?.avatar_url || userData.avatar_url || authUser?.user_metadata?.avatar_url || authUser?.user_metadata?.picture || ''
  })
  
  const [locationData, setLocationData] = useState({})
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)

  // Redirect if no user (but wait for auth to finish loading first)
  useEffect(() => {
    if (authLoading) return
    if (!authUser) {
      navigate('/login')
      return
    }
    // If user already has a complete profile and got here by mistake, redirect them
    if (existingProfile?.username && existingProfile?.display_name) {
      navigate(`/profile/${existingProfile.username}`, { replace: true })
    }
  }, [authUser, authLoading, existingProfile, navigate])

  const validateUsername = (username) => {
    return username.match(/^[a-zA-Z0-9_]{3,20}$/)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })

    // Clear errors when typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' })
    }
  }

  const handleLocationChange = (location) => {
    setLocationData(location)

    // Update formData for backward compatibility with existing validation
    setFormData(prev => ({
      ...prev,
      country: location.country_name || "",
      city: location.city_locality || "",
    }))

    // Clear location-related errors
    if (errors.country) setErrors(prev => ({ ...prev, country: "" }))
    if (errors.city) setErrors(prev => ({ ...prev, city: "" }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validation
    const newErrors = {}
    if (!formData.username) newErrors.username = "Username is required"
    else if (!validateUsername(formData.username)) newErrors.username = "Username must be 3-20 characters, letters/numbers/underscore only"
    
    if (!formData.display_name) newErrors.display_name = "Display name is required"
    
    setErrors(newErrors)
    
    if (Object.keys(newErrors).length > 0) return
    
    try {
      setLoading(true)
      
      // Check if username is already taken (only if creating new profile or changing username)
      const isUsernameChanged = existingProfile ? formData.username !== existingProfile.username : true
      if (isUsernameChanged) {
        const { data: takenProfile } = await profileAPI.getProfile(formData.username)
        if (takenProfile && takenProfile.id !== authUser.id) {
          setErrors({ username: "Username is already taken" })
          setLoading(false)
          return
        }
      }
      
      // Create or update profile
      const profileData = {
        ...(existingProfile ? {} : { id: authUser.id }), // Only include id for new profiles
        username: formData.username,
        display_name: formData.display_name,
        email: authUser.email,
        avatar_url: formData.avatar_url || null,
        // Include location data with proper field mapping
        country: locationData.country_name || formData.country,
        city: locationData.city_locality || formData.city,
        country_code: locationData.country_code,
        country_name: locationData.country_name,
        region_code: locationData.region_code,
        region_name: locationData.region_name,
        city_locality: locationData.city_locality,
        city_sublocality: locationData.city_sublocality,
        postal_code: locationData.postal_code,
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        city_place_id: locationData.city_place_id,
        location_source: locationData.location_source,
        location_confidence: locationData.location_confidence,
      }
      
      let result
      if (existingProfile) {
        // Update existing profile
        console.log('Updating existing profile:', existingProfile.id)
        result = await profileAPI.updateProfile(existingProfile.id, profileData)
      } else {
        // Create new profile
        console.log('Creating new profile for user:', authUser.id)
        result = await profileAPI.createProfile(profileData)
      }
      
      const { error } = result
      
      if (error) {
        console.error('Error creating/updating profile:', error)
        setErrors({ submit: `Failed to ${existingProfile ? 'update' : 'create'} profile. Please try again.` })
        return
      }
      
      // Refresh the user profile in AuthContext
      await loadUserProfile(authUser.id)
      
      // Success! Redirect to user's profile
      navigate(`/profile/${formData.username}`)
      
    } catch (error) {
      console.error('Error creating/updating profile:', error)
      
      // Check if it's a database constraint error (duplicate username, etc.)
      if (error.message?.includes('duplicate') || error.code === '23505') {
        setErrors({ username: 'Username is already taken' })
      } else {
        setErrors({ submit: `Profile ${existingProfile ? 'update' : 'creation'} failed: ${error.message || 'Please try again.'}` })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark p-4">
      <PageMeta title="Profile Setup | Lilycove" description="Set up your Lilycove profile." />
      {/* Back arrow */}
      <button
        onClick={() => navigate(user ? '/' : '/login')}
        className="absolute top-6 left-6 flex items-center justify-center w-10 h-10 text-cream hover:text-purple hover:bg-dark/50 rounded-lg transition-colors"
      >
        <ArrowLeft size={20} />
      </button>

      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-cream mb-2">{existingProfile ? 'Complete Your Profile' : 'Complete Your Profile'}</h1>
          <p className="text-cream/70">
            {existingProfile ? 'Finish setting up your profile information' : 'Set up your profile to start building your Pokémon card collection'}
          </p>
        </div>

        {/* Profile Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Avatar Preview */}
          {formData.avatar_url && (
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-purple/20">
                <img
                  src={formData.avatar_url}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}

          {/* Username */}
          <div className="space-y-2">
            <label htmlFor="username" className="text-cream text-sm">
              Username *
            </label>
            <input
              id="username"
              name="username"
              type="text"
              value={formData.username}
              onChange={handleInputChange}
              placeholder="Choose a unique username"
              className="w-full bg-dark border border-purple/30 text-cream rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple focus:border-purple placeholder:text-cream/50"
            />
            {errors.username && <p className="text-purple text-xs mt-1">{errors.username}</p>}
          </div>

          {/* Display Name */}
          <div className="space-y-2">
            <label htmlFor="display_name" className="text-cream text-sm">
              Your Name *
            </label>
            <input
              id="display_name"
              name="display_name"
              type="text"
              value={formData.display_name}
              onChange={handleInputChange}
              placeholder="Your display name"
              className="w-full bg-dark border border-purple/30 text-cream rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple focus:border-purple placeholder:text-cream/50"
            />
            {errors.display_name && <p className="text-purple text-xs mt-1">{errors.display_name}</p>}
          </div>

          {/* Location */}
          <div className="space-y-4">
            <LocationPicker
              value={locationData}
              onChange={handleLocationChange}
            />
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="text-purple text-sm text-center bg-purple/10 border border-purple/30 rounded-lg p-3">
              {errors.submit}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-purple hover:bg-purple/80 disabled:bg-purple/50 disabled:cursor-not-allowed text-cream py-2 rounded-lg font-medium transition-colors"
          >
            {loading ? `${existingProfile ? 'Updating' : 'Creating'} Profile...` : `${existingProfile ? 'Update' : 'Complete'} Setup`}
          </button>
        </form>

        {/* Note */}
        <div className="text-center mt-6">
          <p className="text-cream/50 text-sm">
            You can update your profile information later in settings
          </p>
        </div>
      </div>
    </div>
  )
}

export default ProfileSetup
